import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import "./Formlayout.css";

const getPageContent = (pathname) => {
  if (pathname === "/create-user") {
    return {
      badge: "Start Your Business Journey",
      title: "Create your premium business workspace.",
      subtitle:
        "Launch your store, manage sales, inventory, payroll, reports, and operations from one elegant platform.",
      metricOne: "2 min",
      metricOneLabel: "Setup time",
      metricTwo: "360°",
      metricTwoLabel: "Business control",
      metricThree: "Pro",
      metricThreeLabel: "Owner access",
    };
  }

  if (pathname === "/forgot-password") {
    return {
      badge: "Secure Recovery",
      title: "Recover access with confidence.",
      subtitle:
        "Reset your password safely and return to managing your business without friction.",
      metricOne: "Safe",
      metricOneLabel: "Recovery",
      metricTwo: "Fast",
      metricTwoLabel: "Reset flow",
      metricThree: "24/7",
      metricThreeLabel: "Access",
    };
  }

  return {
    badge: "Premium Business Access",
    title: "The future of business management starts here.",
    subtitle:
      "Securely access your POS, inventory, invoices, payroll, reports, and daily operations.",
    metricOne: "Secure",
    metricOneLabel: "Login",
    metricTwo: "Smart",
    metricTwoLabel: "Reports",
    metricThree: "Fast",
    metricThreeLabel: "Operations",
  };
};

const FormLayout = ({ children, logoSrc }) => {
  const location = useLocation();
  const content = getPageContent(location.pathname);

  return (
    <div className="auth-v2-page">
      <style>{formLayoutStyles}</style>

      <div className="auth-v2-shell">
        <aside className="auth-v2-visual">
          <div className="auth-v2-brand">
            {logoSrc ? (
              <img src={logoSrc} alt="Paisa Vara" className="auth-v2-logo-img" />
            ) : (
              <div className="auth-v2-logo-mark">PV</div>
            )}
            <div>
              <div className="auth-v2-brand-title">Paisa Vara</div>
              <div className="auth-v2-brand-subtitle">Business Operating Suite</div>
            </div>
          </div>

          <div className="auth-v2-graphic">
            <div className="auth-v2-orbit auth-v2-orbit-one" />
            <div className="auth-v2-orbit auth-v2-orbit-two" />

            <div className="auth-v2-dashboard-card auth-v2-card-main">
              <div className="auth-v2-card-top">
                <span />
                <span />
                <span />
              </div>
              <div className="auth-v2-chart-row">
                <i style={{ height: "48%" }} />
                <i style={{ height: "72%" }} />
                <i style={{ height: "58%" }} />
                <i style={{ height: "86%" }} />
                <i style={{ height: "66%" }} />
              </div>
              <div className="auth-v2-mini-lines">
                <span />
                <span />
                <span />
              </div>
            </div>

            <div className="auth-v2-floating-card auth-v2-float-one">
              <strong>₹</strong>
              <span>Revenue</span>
            </div>

            <div className="auth-v2-floating-card auth-v2-float-two">
              <strong>POS</strong>
              <span>Live Sales</span>
            </div>

            <div className="auth-v2-floating-card auth-v2-float-three">
              <strong>AI</strong>
              <span>Smart Reports</span>
            </div>
          </div>

          <div className="auth-v2-copy">
            <div className="auth-v2-badge">{content.badge}</div>
            <h1>{content.title}</h1>
            <p>{content.subtitle}</p>
          </div>

          <div className="auth-v2-metrics">
            <div>
              <strong>{content.metricOne}</strong>
              <span>{content.metricOneLabel}</span>
            </div>
            <div>
              <strong>{content.metricTwo}</strong>
              <span>{content.metricTwoLabel}</span>
            </div>
            <div>
              <strong>{content.metricThree}</strong>
              <span>{content.metricThreeLabel}</span>
            </div>
          </div>
        </aside>

        <main className="auth-v2-form-side">
          <div className="auth-v2-form-card">{children}</div>
        </main>
      </div>
    </div>
  );
};

FormLayout.propTypes = {
  children: PropTypes.node.isRequired,
  logoSrc: PropTypes.string,
};

FormLayout.defaultProps = {
  logoSrc: "",
};

export default FormLayout;

const formLayoutStyles = `
  .auth-v2-page {
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 26px;
    box-sizing: border-box;
    background:
      linear-gradient(rgba(15, 23, 42, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(15, 23, 42, 0.035) 1px, transparent 1px),
      radial-gradient(circle at 15% 15%, rgba(13, 83, 47, 0.11), transparent 28%),
      radial-gradient(circle at 85% 10%, rgba(217, 119, 6, 0.10), transparent 26%),
      #eef3ff;
    background-size: 22px 22px, 22px 22px, auto, auto, auto;
  }

  .auth-v2-shell {
    width: min(1210px, 100%);
    min-height: min(760px, calc(100vh - 56px));
    display: grid;
    grid-template-columns: minmax(440px, 1.03fr) minmax(420px, 0.97fr);
    border-radius: 28px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid rgba(226, 232, 240, 0.95);
    box-shadow: 0 32px 92px rgba(15, 23, 42, 0.17);
  }

  .auth-v2-visual {
    position: relative;
    overflow: hidden;
    padding: 34px;
    color: #ffffff;
    background:
      radial-gradient(circle at 18% 15%, rgba(250, 204, 21, 0.15), transparent 24%),
      radial-gradient(circle at 90% 22%, rgba(34, 197, 94, 0.14), transparent 25%),
      linear-gradient(135deg, #020617 0%, #0f241b 48%, #0d532f 100%);
  }

  .auth-v2-visual::before {
    content: "";
    position: absolute;
    width: 520px;
    height: 520px;
    left: -240px;
    bottom: -260px;
    border-radius: 999px;
    background: rgba(34, 197, 94, 0.10);
  }

  .auth-v2-visual::after {
    content: "";
    position: absolute;
    width: 280px;
    height: 280px;
    right: -90px;
    top: -120px;
    border-radius: 999px;
    background: rgba(245, 158, 11, 0.14);
  }

  .auth-v2-brand,
  .auth-v2-copy,
  .auth-v2-metrics,
  .auth-v2-graphic {
    position: relative;
    z-index: 1;
  }

  .auth-v2-brand {
    display: flex;
    align-items: center;
    gap: 13px;
  }

  .auth-v2-logo-img {
    width: 54px;
    height: 54px;
    object-fit: contain;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.94);
    padding: 6px;
  }

  .auth-v2-logo-mark {
    width: 54px;
    height: 54px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #052e1a;
    background: linear-gradient(135deg, #fef3c7, #fbbf24);
    font-size: 17px;
    font-weight: 950;
  }

  .auth-v2-brand-title {
    font-size: 18px;
    font-weight: 950;
    letter-spacing: -0.03em;
  }

  .auth-v2-brand-subtitle {
    color: rgba(255, 255, 255, 0.68);
    font-size: 11px;
    font-weight: 800;
  }

  .auth-v2-graphic {
    height: 270px;
    margin: 44px 0 30px;
  }

  .auth-v2-orbit {
    position: absolute;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.13);
  }

  .auth-v2-orbit-one {
    width: 270px;
    height: 270px;
    left: 58px;
    top: 0;
  }

  .auth-v2-orbit-two {
    width: 190px;
    height: 190px;
    left: 98px;
    top: 42px;
  }

  .auth-v2-dashboard-card {
    position: absolute;
    left: 64px;
    top: 34px;
    width: 260px;
    height: 178px;
    border-radius: 22px;
    padding: 18px;
    background: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.17);
    backdrop-filter: blur(16px);
    box-shadow: 0 28px 70px rgba(0, 0, 0, 0.22);
  }

  .auth-v2-card-top {
    display: flex;
    gap: 6px;
  }

  .auth-v2-card-top span {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.65);
  }

  .auth-v2-chart-row {
    height: 86px;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-top: 22px;
  }

  .auth-v2-chart-row i {
    width: 28px;
    border-radius: 9px 9px 4px 4px;
    background: linear-gradient(180deg, #fbbf24, #22c55e);
    box-shadow: 0 12px 22px rgba(34, 197, 94, 0.16);
  }

  .auth-v2-mini-lines {
    display: grid;
    gap: 7px;
    margin-top: 17px;
  }

  .auth-v2-mini-lines span {
    height: 7px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
  }

  .auth-v2-mini-lines span:nth-child(2) {
    width: 72%;
  }

  .auth-v2-mini-lines span:nth-child(3) {
    width: 48%;
  }

  .auth-v2-floating-card {
    position: absolute;
    min-width: 118px;
    padding: 13px 14px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.17);
    backdrop-filter: blur(14px);
    box-shadow: 0 18px 42px rgba(0, 0, 0, 0.18);
  }

  .auth-v2-floating-card strong {
    display: block;
    color: #fef3c7;
    font-size: 18px;
    font-weight: 950;
    line-height: 1;
  }

  .auth-v2-floating-card span {
    display: block;
    margin-top: 6px;
    color: rgba(255, 255, 255, 0.70);
    font-size: 11px;
    font-weight: 850;
  }

  .auth-v2-float-one {
    left: 6px;
    top: 132px;
  }

  .auth-v2-float-two {
    right: 26px;
    top: 44px;
  }

  .auth-v2-float-three {
    right: 64px;
    bottom: 4px;
  }

  .auth-v2-badge {
    display: inline-flex;
    padding: 7px 12px;
    border-radius: 999px;
    margin-bottom: 13px;
    color: #fde68a;
    font-size: 10px;
    font-weight: 950;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.11);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }

  .auth-v2-copy h1 {
    max-width: 560px;
    margin: 0;
    font-size: clamp(36px, 4.6vw, 58px);
    line-height: 0.98;
    font-weight: 950;
    letter-spacing: -0.07em;
  }

  .auth-v2-copy p {
    max-width: 520px;
    margin: 16px 0 0;
    color: rgba(255, 255, 255, 0.76);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.7;
  }

  .auth-v2-metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 30px;
  }

  .auth-v2-metrics div {
    padding: 13px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  .auth-v2-metrics strong {
    display: block;
    font-size: 15px;
    font-weight: 950;
  }

  .auth-v2-metrics span {
    display: block;
    margin-top: 4px;
    color: rgba(255, 255, 255, 0.62);
    font-size: 10px;
    font-weight: 850;
  }

  .auth-v2-form-side {
    min-width: 0;
    background:
      radial-gradient(circle at top right, rgba(13, 83, 47, 0.08), transparent 30%),
      #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
    overflow: auto;
  }

  .auth-v2-form-card {
    width: 100%;
    max-width: 455px;
  }

  @media (max-width: 980px) {
    .auth-v2-page {
      padding: 16px;
      align-items: flex-start;
    }

    .auth-v2-shell {
      grid-template-columns: 1fr;
    }

    .auth-v2-visual {
      min-height: 560px;
    }
  }

  @media (max-width: 560px) {
    .auth-v2-page {
      padding: 10px;
    }

    .auth-v2-shell {
      border-radius: 22px;
    }

    .auth-v2-visual,
    .auth-v2-form-side {
      padding: 20px;
    }

    .auth-v2-graphic {
      height: 230px;
      margin: 28px 0 24px;
    }

    .auth-v2-dashboard-card {
      left: 34px;
      width: 220px;
    }

    .auth-v2-floating-card {
      min-width: 96px;
    }

    .auth-v2-metrics {
      grid-template-columns: 1fr;
    }
  }
`;
